'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { productsApi, authApi } from '@/lib/api';
import { Product, Category } from '@/types';
import { getImageUrl } from '@/lib/utils';
import Link from 'next/link';
import { useCart } from '@/contexts/CartContext';
import PriceTable, { hasPriceTableOptions } from '@/components/PriceTable';

export default function MenuPage() {
  const router = useRouter();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { getCartItemsCount } = useCart();
  const cartItemsCount = getCartItemsCount();

  useEffect(() => {
    loadMenu();
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('amam_token');
      if (token) {
        await authApi.getProfile();
        setIsAuthenticated(true);
      }
    } catch (error) {
      setIsAuthenticated(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('amam_token');
    localStorage.removeItem('amam_user');
    setIsAuthenticated(false);
    setUserMenuOpen(false);
    router.push('/');
  };

  const loadMenu = async () => {
    try {
      setLoading(true);
      const [productsRes, categoriesRes] = await Promise.all([
        productsApi.getProducts(),
        productsApi.getCategories(),
      ]);

      setProducts(productsRes.data.filter((p: Product) => p.isAvailable));
      setCategories(categoriesRes.data.filter((c: Category) => c.isActive));
    } catch (error) {
      console.error('Failed to load menu:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = selectedCategory
    ? products.filter((p) => p.category._id === selectedCategory)
    : products;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">טוען תפריט...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          {/* Mobile: Line 1 - Back and Cart */}
          <div className="flex md:hidden items-center justify-between pb-3 border-b border-gray-100">
            <Link href="/" className="text-blue-600 hover:underline">
              → חזרה לדף הבית
            </Link>
            <Link
              href="/cart"
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition relative"
            >
              🛒 סל קניות
              {cartItemsCount > 0 && (
                <span className="absolute -top-2 -left-2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {cartItemsCount > 99 ? '99+' : cartItemsCount}
                </span>
              )}
            </Link>
          </div>
          {/* Mobile: Line 2 / Desktop: Single line */}
          <div className="flex items-center justify-between pt-3 md:pt-0">
            <h1 className="text-2xl font-bold text-gray-900">תפריט AmAm</h1>
            <div className="flex items-center gap-4">
              {/* Cart - Desktop only */}
              <Link
                href="/cart"
                className="hidden md:flex bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition relative"
              >
                🛒 סל קניות
                {cartItemsCount > 0 && (
                  <span className="absolute -top-2 -left-2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {cartItemsCount > 99 ? '99+' : cartItemsCount}
                  </span>
                )}
              </Link>

              {/* User Menu */}
              {isAuthenticated ? (
                <div className="relative">
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-2 text-gray-700 hover:text-blue-600 font-medium px-4 py-2 rounded-lg hover:bg-gray-100 transition"
                  >
                    👤 החשבון שלי
                  </button>
                  {userMenuOpen && (
                    <div className="absolute left-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-2 border z-[100]">
                      <Link
                        href="/account"
                        className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        לוח בקרה
                      </Link>
                      <Link
                        href="/account/orders"
                        className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        רכישות קודמות
                      </Link>
                      <Link
                        href="/account/addresses"
                        className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        כתובות שלי
                      </Link>
                      <Link
                        href="/account/favorites"
                        className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        מועדפים שלי
                      </Link>
                      <Link
                        href="/account/profile"
                        className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        פרופיל
                      </Link>
                      <hr className="my-2" />
                      <button
                        onClick={handleLogout}
                        className="block w-full text-right px-4 py-2 text-red-600 hover:bg-gray-100"
                      >
                        התנתק
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <Link
                    href="/login"
                    className="text-gray-700 hover:text-blue-600 font-medium px-4 py-2 rounded-lg hover:bg-gray-100 transition"
                  >
                    התחבר
                  </Link>
                  <Link
                    href="/register"
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                  >
                    הירשם
                  </Link>
                </div>
              )}

              {/* Back to home - Desktop only */}
              <Link
                href="/"
                className="hidden md:block text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-100 transition"
              >
                חזרה לדף הבית
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Categories Filter */}
      <div className="bg-white border-b sticky top-[136px] md:top-16 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex gap-2 overflow-x-auto">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-4 py-2 rounded-full whitespace-nowrap transition ${
                selectedCategory === null
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              הכל
            </button>
            {categories.map((category) => (
              <button
                key={category._id}
                onClick={() => setSelectedCategory(category._id)}
                className={`px-4 py-2 rounded-full whitespace-nowrap transition ${
                  selectedCategory === category._id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category.nameHe || category.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <main className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {filteredProducts.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">אין מוצרים זמינים כרגע</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProducts.map((product) => (
              <Link
                key={product._id}
                href={`/product/${product._id}`}
                className="bg-white rounded-lg shadow-md hover:shadow-lg transition overflow-hidden group"
              >
                {/* Product Image */}
                <div className="relative h-48 bg-gray-200 overflow-hidden">
                  {product.image ? (
                    <img
                      src={getImageUrl(product.image)}
                      alt={product.nameHe || product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <span className="text-4xl">🍽️</span>
                    </div>
                  )}
                  {/* Favorite indicator - shows on hover */}
                  <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span
                      className="bg-white/90 rounded-full p-2 shadow-md inline-block"
                      title="לחץ כדי לראות פרטים ולשמור למועדפים"
                    >
                      🤍
                    </span>
                  </div>
                </div>

                {/* Product Info */}
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {product.nameHe || product.name}
                  </h3>
                  {product.descriptionHe && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {product.descriptionHe}
                    </p>
                  )}
                  {/* Price Display - Show price table if options are marked for it, otherwise show base price */}
                  {hasPriceTableOptions(product) ? (
                    <div className="mt-2">
                      <PriceTable product={product} />
                      {(product.preparationTime ?? 0) > 0 && (
                        <div className="text-sm text-gray-500 mt-1">
                          ⏱️ {product.preparationTime} דק'
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between">
                        <span className="text-2xl font-bold text-blue-600">
                          ₪{product.price.toFixed(2)}
                        </span>
                        {(product.preparationTime ?? 0) > 0 && (
                          <span className="text-sm text-gray-500">
                            ⏱️ {product.preparationTime} דק'
                          </span>
                        )}
                      </div>
                      {product.options && product.options.length > 0 && (
                        <div className="mt-2 text-xs text-gray-500">
                          ✨ ניתן להתאמה אישית
                        </div>
                      )}
                    </>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
